// src/config/menuConfig.js
//
// Static menu definition that mirrors the exact JSON shape the backend will
// eventually return. When the API is ready, this file becomes the fallback only.
//
// KEY DESIGN DECISIONS:
//   - moduleCode: stable string ID for the module (used for icon lookup)
//   - pageAccessKey: stable page code from pageCodes.js (used for access control)
//   - icon: NOT stored here — derived at runtime from MODULE_ICON_MAP[moduleCode]
//
// STRUCTURE:
//   Module
//     └── directPages   (pages with no submodule parent)
//     └── submodules
//           └── pages   (leaf items)

import * as PC from "./pageCodes";

const menuConfig = {
  modules: [
    // ─────────────────────────────────────────────
    // NORMAL — Application (Dashboard)
    // ─────────────────────────────────────────────
    {
      id: 1,
      moduleCode: PC.MOD_APP,
      name: "Dashboard",
      seq: 1,
      isActive: true,
      mode: "NORMAL",
      submodules: [],
      directPages: [
        {
          id: 101,
          name: "Overview",
          route: "/dashboard",
          seq: 1,
          code: "Dash_1",
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
      ],
    },

    // ─────────────────────────────────────────────
    // SETTINGS — Settings (CLIENT org users only)
    // Module → Submodule (Account/Manage Access/Manage Masters) → Pages. Multi-page rows link to a tabbed container under src/pages/Settings/<name>.
    // ─────────────────────────────────────────────
    {
      id: 10,
      moduleCode: PC.MOD_SETTINGS,
      name: "Settings",
      // Highest seq in the file on purpose — always sorts last
      seq: 100,
      isActive: true,
      mode: "NORMAL",
      clientOnly: true,
      directPages: [],
      submodules: [
        {
          id: 9010,
          name: "Account",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 90101,
              name: "Org Profile",
              route: "/client/account",
              seq: 1,
              submoduleId: 9010,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90102,
              name: "Subscription",
              route: "/settings/subscription",
              seq: 2,
              submoduleId: 9010,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90103,
              name: "Personalization",
              route: "/settings/personalization",
              seq: 3,
              submoduleId: 9010,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 9020,
          name: "Manage Access",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 90203,
              name: "Role & Access Profile",
              route: "/settings/roles",
              seq: 1,
              submoduleId: 9020,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90202,
              name: "User Groups",
              route: "/user_group",
              seq: 2,
              submoduleId: 9020,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90201,
              name: "Users",
              route: "/settings/users",
              seq: 3,
              submoduleId: 9020,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 9030,
          name: "Manage Masters",
          seq: 3,
          isActive: true,
          pages: [
            {
              id: 90301,
              name: "Locations",
              route: "/settings/locations",
              seq: 1,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90302,
              name: "Markets",
              route: "/hub",
              seq: 2,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90303,
              name: "Banks",
              route: "/settings/banks",
              seq: 3,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90304,
              name: "Nodal Points",
              route: "/settings/nodal-points",
              seq: 4,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90305,
              name: "Payment Gateways",
              route: "/payment-gateways",
              seq: 5,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90306,
              name: "Product Categories",
              route: "/settings/product-categories",
              seq: 6,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90307,
              name: "Entities",
              route: "/settings/entities",
              seq: 7,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 90308,
              name: "Seller Entities",
              route: "/settings/seller-entities",
              seq: 8,
              submoduleId: 9030,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // SETTINGS — Account (platform users — branding & personalization)
    // ─────────────────────────────────────────────
    {
      id: 2,
      moduleCode: PC.MOD_ACCT,
      name: "Account",
      seq: 1,
      isActive: true,
      mode: "SETTINGS",
      platformOnly: true,
      directPages: [],
      submodules: [
        {
          id: 20,
          name: "Branding & Personalization",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 201,
              name: "Organizations",
              route: "/organization",
              seq: 1,
              submoduleId: 20,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 202,
              name: "Applications",
              route: "/application",
              seq: 2,
              submoduleId: 20,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 203,
              name: "Organization & Application Mapping",
              route: "/org-app-mapping",
              seq: 3,
              submoduleId: 20,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 204,
              name: "Theme Personalization",
              route: "/theme-personalization/create",
              seq: 4,
              submoduleId: 20,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // SETTINGS — Module Personalization (platform users)
    // ─────────────────────────────────────────────
    {
      id: 3,
      moduleCode: PC.MOD_MOD_PERS,
      name: "Module Personalization",
      seq: 3,
      isActive: true,
      mode: "SETTINGS",
      platformOnly: true,
      submodules: [],
      directPages: [
        {
          id: 301,
          name: "Module",
          route: "/module",
          seq: 1,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 302,
          name: "Sub Module",
          route: "/subModule",
          seq: 2,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 303,
          name: "Pages",
          route: "/pages",
          seq: 3,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 304,
          name: "Module Organization Mapping",
          route: "/moduleOrgMapping",
          seq: 4,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 305,
          name: "Sub Module Organization Mapping",
          route: "/subModuleOrgMapping",
          seq: 5,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 306,
          name: "Page Organization Mapping",
          route: "/pageOrgMapping",
          seq: 6,
          submoduleId: null,
          isActive: true,
          pageAccessKey: null,
        },
      ],
    },

    // ─────────────────────────────────────────────
    // SETTINGS — Users & Access (platform users)
    // ─────────────────────────────────────────────
    {
      id: 4,
      moduleCode: PC.MOD_USR_ACCESS,
      name: "Users & Access",
      seq: 4,
      isActive: true,
      mode: "SETTINGS",
      platformOnly: true,
      directPages: [],
      submodules: [
        {
          id: 40,
          name: "Access Management",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 401,
              name: "Roles",
              route: "/user_role",
              seq: 1,
              submoduleId: 40,
              isActive: true,
              pageAccessKey: PC.USR_ACCESS_ROLE_CREATE,
            },
            {
              id: 402,
              name: "Page Creation",
              route: "/page_creation",
              seq: 2,
              submoduleId: 40,
              isActive: true,
              pageAccessKey: PC.USR_ACCESS_PAGE_CREATE,
            },
            {
              id: 403,
              name: "Page Action Mapping",
              route: "/page_mapping",
              seq: 3,
              submoduleId: 40,
              isActive: true,
              pageAccessKey: PC.USR_ACCESS_PAGE_ACTION,
            },
            {
              id: 404,
              name: "Role Action Mapping",
              route: "/role_action_mapping",
              seq: 4,
              submoduleId: 40,
              isActive: false,
              pageAccessKey: PC.USR_ACCESS_ROLE_ACTION,
            },
            {
              id: 405,
              name: "Page Access Profile Mapping",
              route: "/page_access_profile_mapping",
              seq: 5,
              submoduleId: 40,
              isActive: true,
              pageAccessKey: PC.USR_ACCESS_PAGE_PLT_ROLE,
            },
            {
              id: 407,
              name: "Role & Access Profile Mapping",
              route: "/role_access_profile_mapping",
              seq: 7,
              submoduleId: 40,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 41,
          name: "Users",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 411,
              name: "Users",
              route: "/user_profile",
              seq: 1,
              submoduleId: 41,
              isActive: true,
              pageAccessKey: PC.USR_USERS_ORG,
            },
            {
              id: 412,
              name: "Sales Agents",
              route: "/az_partner",
              seq: 2,
              submoduleId: 41,
              isActive: true,
              pageAccessKey: PC.USR_USERS_AZ_PARTNER,
            },
            {
              id: 413,
              name: "Sellers",
              route: "/admin_seller",
              seq: 3,
              submoduleId: 41,
              isActive: true,
              pageAccessKey: PC.USR_USERS_SELLERS,
            },
            {
              id: 414,
              name: "Customer",
              route: "/admin_customer",
              seq: 4,
              submoduleId: 41,
              isActive: true,
              pageAccessKey: PC.USR_USERS_CUSTOMER,
            },
            {
              id: 415,
              name: "Shippers",
              route: "/shippers",
              seq: 5,
              submoduleId: 41,
              isActive: true,
              pageAccessKey: PC.USR_USERS_SHIPPERS,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // SETTINGS — Manage Master (platform users only; clients use id:9030 above)
    // Commented out — superseded by the Settings > Manage Masters submodule (id:9030)
    // ─────────────────────────────────────────────
    /* {
      id: 5,
      moduleCode: PC.MOD_MASTER,
      name: "Manage Master",
      seq: 5,
      isActive: true,
      mode: "SETTINGS",
      platformOnly: true,
      directPages: [],
      submodules: [
        {
          id: 50,
          name: "Locations",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 501,
              name: "States",
              route: "/state",
              code: "State_1",
              seq: 1,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 502,
              name: "District",
              route: "/district",
              seq: 2,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 503,
              name: "Sub District",
              route: "/subdistrict",
              seq: 3,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 504,
              name: "Pin Code",
              route: "/pincode",
              seq: 4,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 505,
              name: "Village",
              route: "/village",
              seq: 5,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 506,
              name: "Block",
              route: "/block",
              seq: 6,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 507,
              name: "Market",
              route: "/hub",
              seq: 7,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 508,
              name: "Country",
              route: "/country",
              seq: 8,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 509,
              name: "Block-Village Mapping",
              route: "/block-village-mapping",
              seq: 9,
              submoduleId: 50,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 51,
          name: "Categories",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 511,
              name: "Category",
              route: "/category",
              seq: 1,
              submoduleId: 51,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 512,
              name: "Sub Category",
              route: "/subcategory",
              seq: 2,
              submoduleId: 51,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 513,
              name: "Category Location Mapping",
              route: "/category-location-mapping",
              seq: 3,
              submoduleId: 51,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 514,
              name: "SubCategory Location Mapping",
              route: "/subcategory-location-mapping",
              seq: 4,
              submoduleId: 51,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 52,
          name: "Bank",
          seq: 3,
          isActive: true,
          pages: [
            {
              id: 521,
              name: "Bank",
              route: "/bank",
              seq: 1,
              submoduleId: 52,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 522,
              name: "Branch",
              route: "/branch",
              seq: 2,
              submoduleId: 52,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 53,
          name: "Organisations",
          seq: 4,
          isActive: true,
          pages: [
            {
              id: 531,
              name: "Organisation Type",
              route: "/organizationtype",
              seq: 1,
              submoduleId: 53,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 532,
              name: "Legal Entities",
              route: "/legalentity",
              seq: 2,
              submoduleId: 53,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 54,
          name: "Nodal Points",
          seq: 5,
          isActive: true,
          pages: [
            {
              id: 541,
              name: "Fulfillment Center",
              route: "/fulfillment_center",
              seq: 1,
              submoduleId: 54,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 542,
              name: "Delivery Point",
              route: "/delivery-point",
              seq: 2,
              submoduleId: 54,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    }, */

    // ─────────────────────────────────────────────
    // NORMAL — Catalogue
    // ─────────────────────────────────────────────
    {
      id: 6,
      moduleCode: PC.MOD_CATALOGUE,
      name: "Catalogue",
      seq: 6,
      isActive: true,
      mode: "NORMAL",
      directPages: [],
      submodules: [
        {
          id: 60,
          name: "Products",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 601,
              name: "Master Products Catalogue",
              route: "/product",
              seq: 1,
              submoduleId: 60,
              isActive: true,
              pageAccessKey: PC.CAT_PROD_MASTER,
            },
            {
              id: 602,
              name: "Products",
              route: "/product_variant",
              seq: 2,
              submoduleId: 60,
              isActive: true,
              pageAccessKey: PC.CAT_PROD_VARIANTS,
            },
            {
              id: 603,
              name: "Pending Online Approval",
              route: "/seller_product_variant",
              seq: 3,
              submoduleId: 60,
              isActive: true,
              pageAccessKey: PC.CAT_PROD_PENDING_ONLINE,
            },
            {
              id: 604,
              name: "Live Market Products",
              route: "/online_seller_product_variant",
              seq: 4,
              submoduleId: 60,
              isActive: true,
              pageAccessKey: PC.CAT_PROD_LIVE_MARKET,
            },
            {
              id: 605,
              name: "New Product Onboarding Request",
              route: "/product_activation",
              seq: 5,
              submoduleId: 60,
              isActive: true,
              pageAccessKey: PC.CAT_PROD_NEW_REQUESTS,
            },
          ],
        },
        {
          id: 61,
          name: "Promos",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 611,
              name: "Promos",
              route: "/promotion_products",
              seq: 1,
              submoduleId: 61,
              isActive: true,
              pageAccessKey: PC.CAT_PROMO_LIST,
            },
            {
              id: 612,
              name: "Live Promos",
              route: "/online_promotion_products",
              seq: 2,
              submoduleId: 61,
              isActive: true,
              pageAccessKey: PC.CAT_PROMO_LIVE,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // NORMAL — Fulfillment
    // ─────────────────────────────────────────────
    {
      id: 7,
      moduleCode: PC.MOD_FULFILL,
      name: "Fulfillment",
      seq: 7,
      isActive: true,
      mode: "NORMAL",

      submodules: [
        {
          id: 70,
          name: "Instant Fulfillment",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 701,
              name: "Order Dashboard",
              route: "/order_listing",
              seq: 1,
              submoduleId: 70,
              isActive: true,
              pageAccessKey: PC.FULFILL_INST_ORDER_DASHBOARD,
            },
            {
              id: 702,
              name: "Lead Dashboard",
              route: "/off_platform_orders",
              seq: 2,
              submoduleId: 70,
              isActive: true,
              pageAccessKey: PC.FULFILL_INST_LEAD_DASHBOARD,
            },
            {
              id: 703,
              name: "Create Order",
              route: "/Order/createOrder",
              seq: 3,
              submoduleId: 70,
              isActive: true,
              pageAccessKey: PC.FULFILL_INST_CREATE_ORDER,
            },
          ],
        },
        {
          id: 71,
          name: "Future Fulfillment",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 711,
              name: "Enquiry Dashboard",
              route: "/future_fulfillment",
              seq: 1,
              submoduleId: 71,
              isActive: true,
              pageAccessKey: PC.FULFILL_FF_ENQUIRY,
            },
          ],
        },
        {
          id: 72,
          name: "Fulfillment Center",
          seq: 3,
          isActive: true,
          pages: [
            {
              id: 721,
              name: "Products Movement",
              route: "/material-movement",
              seq: 1,
              submoduleId: 72,
              isActive: true,
              pageAccessKey: PC.FULFILL_FC_MATERIAL_MOVEMENT,
            },
            {
              id: 722,
              name: "Money Movement",
              route: "/money_movement",
              seq: 2,
              submoduleId: 72,
              isActive: true,
              pageAccessKey: PC.FULFILL_FC_MONEY_MOVEMENT,
            },
            {
              id: 723,
              name: "DPP Ledger",
              route: "/reconciliation_ledger",
              seq: 3,
              submoduleId: 72,
              isActive: true,
              pageAccessKey: PC.FULFILL_FC_DPP_LEDGER,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // NORMAL — Finance
    // ─────────────────────────────────────────────
    {
      id: 8,
      moduleCode: PC.MOD_FINANCE,
      name: "Finance",
      displayName: "Accouting",
      seq: 8,
      isActive: true,
      mode: "NORMAL",
      directPages: [
        {
          id: 801,
          name: "Deposit Confirmation",
          displayName: "Payment Confirmation",
          route: "/deposite-approval",
          seq: 1,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.FIN_DEPOSIT_CONFIRM,
        },
        {
          id: 802,
          name: "Sales Commission Invoices & Credit Notes",
          route: "/sales-commission-voucher",
          seq: 2,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.FIN_SALES_COMMISSION,
        },
      ],
      submodules: [],
    },

    // ─────────────────────────────────────────────
    // NORMAL — Reports
    // ─────────────────────────────────────────────
    {
      id: 9,
      moduleCode: PC.MOD_REPORTS,
      name: "Reports",
      seq: 9,
      isActive: true,
      mode: "NORMAL",
      directPages: [
        {
          id: 901,
          name: "Orders",
          route: "/orders-reports",
          seq: 1,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.RPT_ORDERS,
        },
        {
          id: 902,
          name: "Sales",
          route: "/sales-reports",
          seq: 2,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.RPT_SALES,
        },
        {
          id: 903,
          name: "Returns",
          route: "/returns-reports",
          seq: 3,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.RPT_RETURNS,
        },
        {
          id: 904,
          name: "Declines By Shipper",
          route: "/declinedByShipper-reports",
          seq: 4,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.RPT_DECLINES_SHIPPER,
        },
        {
          id: 905,
          name: "Seller Remittance",
          route: "/remmittance-reports",
          seq: 5,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.RPT_SELLER_REMITTANCE,
        },

        {
          id: 7001,
          name: "Audit Logs",
          route: "/auditlogs",
          seq: 99,
          submoduleId: null,
          isActive: true,
          pageAccessKey: PC.FULFILL_AUDIT_LOGS,
        },
      ],
      submodules: [],
    },

    // ─────────────────────────────────────────────
    // NORMAL — Users (moved to Manage Users & Roles in SETTINGS)
    // ─────────────────────────────────────────────
    {
      id: 13,
      moduleCode: PC.MOD_USERS,
      name: "Users",
      seq: 10,
      isActive: false,
      mode: "NORMAL",
      directPages: [],
      submodules: [
        {
          id: 130,
          name: "All Users",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 1301,
              name: "Sales Agents",
              route: "/az_partner",
              seq: 1,
              submoduleId: 130,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 1302,
              name: "Sellers",
              route: "/admin_seller",
              seq: 2,
              submoduleId: 130,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 1303,
              name: "Customers",
              route: "/admin_customer",
              seq: 3,
              submoduleId: 130,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 1304,
              name: "Shippers",
              route: "/shippers",
              seq: 4,
              submoduleId: 130,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    },

    // {
    //   id: 502,
    //   moduleCode: PC.MOD_USR_PROFILE,
    //   name: "User Access Profile",
    //   seq: 3,
    //   isActive: true,
    //   mode: "SETTINGS",
    //   clientOnly: true,
    //   submodules: [],
    //   directPages: [
    //     {
    //       id: 5201,
    //       name: "User Access Profile",
    //       route: "/user_profile",
    //       seq: 1,
    //       isActive: true,
    //       pageAccessKey: null,
    //     },
    //   ],
    // },

    // ─────────────────────────────────────────────
    // PLATFORM — Platform admin portal menus
    // ─────────────────────────────────────────────
    {
      id: 61,
      moduleCode: PC.MOD_PLT_CLIENTS,
      name: "Manage Clients",
      seq: 1,
      isActive: true,
      mode: "PLATFORM",
      platformOnly: true,
      submodules: [],
      directPages: [
        {
          id: 6001,
          name: "Manage Clients",
          route: "/home",
          seq: 1,
          isActive: true,
          pageAccessKey: null,
        },
      ],
    },
    // ─────────────────────────────────────────────
    // PLATFORM — Settings (platform org's own users, mirrors client id:10)
    // Same pages as the client's Manage Access; Users list is scoped to the
    // platform's own org (see Settings/Users ownOrg) instead of cross-org.
    // Replaces the old "Manage Users & Roles" module (id:63) — its Access
    // Profile / Page mapping pages are tabs under Page & Access Profile.
    // Manage Masters mirrors the client's (id:9030) tabbed master pages and
    // replaces the old platform "Manage Master" module (id:62).
    // ─────────────────────────────────────────────
    {
      id: 64,
      moduleCode: PC.MOD_SETTINGS,
      name: "Settings",
      // Highest seq on purpose — always sorts last
      seq: 100,
      isActive: true,
      mode: "PLATFORM",
      platformOnly: true,
      directPages: [],
      submodules: [
        {
          id: 640,
          name: "Manage Access",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 6404,
              name: "Page & Access Profile",
              route: "/settings/access-profiles",
              seq: 1,
              submoduleId: 640,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6403,
              name: "Role & Access Profile",
              route: "/settings/roles",
              seq: 2,
              submoduleId: 640,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6402,
              name: "User Groups",
              route: "/user_group",
              seq: 3,
              submoduleId: 640,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6401,
              name: "Users",
              route: "/settings/users",
              seq: 4,
              submoduleId: 640,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
        {
          id: 650,
          name: "Manage Masters",
          seq: 2,
          isActive: true,
          pages: [
            {
              id: 6501,
              name: "Applications & Modules",
              route: "/settings/applications-modules",
              seq: 1,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6502,
              name: "Locations",
              route: "/settings/locations",
              seq: 2,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6503,
              name: "Markets",
              route: "/hub",
              seq: 3,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6504,
              name: "Banks",
              route: "/settings/banks",
              seq: 4,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6505,
              name: "Nodal Points",
              route: "/settings/nodal-points",
              seq: 5,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6506,
              name: "Product Categories",
              route: "/settings/product-categories",
              seq: 6,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 6507,
              name: "Seller Entities",
              route: "/settings/seller-entities",
              seq: 7,
              submoduleId: 650,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────
    // CLIENT_VIEW — platform user inside a client context
    // ─────────────────────────────────────────────
    {
      id: 700,
      moduleCode: PC.MOD_CV_ACCT,
      name: "Account",
      seq: 1,
      isActive: true,
      mode: "CLIENT_VIEW",
      platformOnly: true,
      submodules: [],
      directPages: [
        {
          id: 7001,
          name: "Org Details",
          route: "/client/account",
          seq: 1,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 7004,
          name: "Payments & Billing",
          route: "/client/account/billing",
          seq: 2,
          isActive: true,
          pageAccessKey: null,
        },
        {
          id: 7005,
          name: "Invoices",
          route: "/client/account/invoices",
          seq: 3,
          isActive: true,
          pageAccessKey: null,
        },
      ],
    },
    {
      id: 701,
      moduleCode: PC.MOD_CV_SUBS,
      name: "Subscription",
      seq: 2,
      isActive: true,
      mode: "CLIENT_VIEW",
      platformOnly: true,
      directPages: [],
      submodules: [
        {
          id: 7110,
          name: "Application & Modules Mapping",
          seq: 1,
          isActive: true,
          pages: [
            {
              id: 71101,
              name: "Application",
              route: "/org-app-mapping",
              seq: 1,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 71102,
              name: "Module",
              route: "/moduleOrgMapping",
              seq: 2,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 71103,
              name: "Submodule",
              route: "/subModuleOrgMapping",
              seq: 3,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 71104,
              name: "Pages",
              route: "/pageOrgMapping",
              seq: 4,
              isActive: true,
              pageAccessKey: null,
            },
            {
              id: 71105,
              name: "Sub Pages",
              route: "/subPageOrgMapping",
              seq: 5,
              isActive: true,
              pageAccessKey: null,
            },
          ],
        },
      ],
    },
  ],
};

export default menuConfig;
